import React, { useEffect, useState } from "react";
import { Form, Input, Select, Row, Col, Button, Drawer } from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import IntlMessages from "util/IntlMessages";
import { countryCodeList, statusArray } from "../../helpers";
import { sortBy } from "lodash";
import {
  useGetProductListQuery,
  useGetSingleProductQuery,
} from "../../appRedux/api/adminProductsApi";
import {
  useGetCountryListQuery,
  useGetSinglePartnerQuery,
  useAddPartnerMutation,
  useEditPartnerMutation,
} from "../../appRedux/api/adminPartnerApi";
import "./style.css";

const { Option } = Select;
const FormItem = Form.Item;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const PartnerDrawer = ({ visible, onClose, editMode, partnerId }) => {
  const [form] = Form.useForm();
  const [partnerEmployeeData, setPartnerEmployeeData] = useState([]);

  // RTK Query hooks
  const { data: productList = { results: [] } } = useGetProductListQuery({ showRecent: false, list: true });
  const { data: countryList = [] } = useGetCountryListQuery();
  const { data: partnerDetails, isSuccess: isPartnerLoaded } = useGetSinglePartnerQuery(
    partnerId,
    { skip: !partnerId || !editMode }
  );
  const [addPartner, { isLoading: isAddLoading }] = useAddPartnerMutation();
  const [editPartner, { isLoading: isEditLoading }] = useEditPartnerMutation();
  const isSubmitting = isAddLoading || isEditLoading;

  useEffect(() => {
    if (isPartnerLoaded && editMode) {
      const { partner } = partnerDetails;
      setPartnerEmployeeData(
        partner && partner.employee ? partner.employee : [],
      );
      form.setFieldsValue({
        company_name: partner.company_name,
        email: partnerDetails.email,
        contact: partner.contact_number.slice(
          partner.contact_number.length - 10,
        ),
        prefix: partner.contact_number.substring(
          0,
          partner.contact_number.length - 10,
        ),
        address: partner.address,
        region: partner.region,
        active_products: partner.active_products,
        employee: partner.employee || [],
      });
    }
  }, [isPartnerLoaded, editMode, partnerDetails, form]);

  const onFinish = async (values) => {
    // Process active_products to include product_name and category
    const processedActiveProducts = values.active_products 
      ? values.active_products.map(product => {
          return {
            ...product,
            product_name: product.product_name || ""
          };
        })
      : [];

    let data = {
      email: values.email ? values.email.toLowerCase() : "",
      partner: {
        company_name: values.company_name,
        contact_number: values.prefix + values.contact,
        address: values.address ? values.address : null,
        region: values.region,
        active_products: processedActiveProducts,
        employee: setEmployeeData(values.employee),
      },
    };

    try {
      if (editMode) {
        await editPartner({ id: partnerId, data }).unwrap();
      } else {
        await addPartner(data).unwrap();
      }
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Failed to save partner:', error);
    }
  };

  const setEmployeeData = (data) => {
    if (data && data.length > 0) {
      if (editMode) {
        return data.map((value, index) => {
          if (partnerEmployeeData[index]) {
            return {
              ...value,
              employee_id: partnerEmployeeData[index].employee_id,
            };
          } else {
            return { ...value, employee_id: 0 };
          }
        });
      } else {
        return data.map((value) => ({ ...value, employee_id: 0 }));
      }
    }
    return [];
  };

  const setCountryCodeListOptions = () => {
    let sortedCountryCodeList = sortBy(countryCodeList, ["code"]);
    return sortedCountryCodeList.map((value) => (
      <Option key={value.code} value={value.code}>
        {value.code}
      </Option>
    ));
  };

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Select
        style={{ width: 100 }}
        showSearch
        optionFilterProp="children"
        filterOption={true}
        id="partner-countrylist"
      >
        {setCountryCodeListOptions()}
      </Select>
    </Form.Item>
  );

  return (
    <Drawer
      title={editMode ? "Edit Licencee" : "Add Licencee"}
      placement="right"
      width={1200}
      onClose={onClose}
      open={visible}
      className="product-drawer"
      closable={true}
      footer={
        <div className="drawer-footer">
          <Button onClick={onClose} style={{ marginRight: 8 }} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={() => form.submit()}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {editMode ? 'Save' : 'Add Licencee'}
          </Button>
        </div>
      }
    >
      <Form
        layout="vertical"
        form={form}
        name="partner-form"
        onFinish={onFinish}
        initialValues={{
          prefix: "+91",
          active_products: [],
          employee: [],
        }}
        scrollToFirstError
        className="partner-form"
      >
        <Form.Item
          name="company_name"
          label="Company Name"
          rules={[
            {
              required: true,
              message: <IntlMessages id="error.company_required" />,
            },
          ]}
        >
          <Input placeholder="Enter company name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="E-mail"
          rules={[
            {
              type: "email",
              message: <IntlMessages id="error.email_valid" />,
            },
            {
              required: true,
              message: <IntlMessages id="error.email_required" />,
            },
          ]}
        >
          <Input placeholder="Enter email" />
        </Form.Item>

        <Form.Item
          name="contact"
          label="Contact Number"
          rules={[
            {
              required: true,
              message: <IntlMessages id="error.contact_required" />,
            },
            {
              len: 10,
              message: <IntlMessages id="error.contact_length" />,
            },
            {
              pattern: /^[0-9]+$/,
              message: <IntlMessages id="error.contact_valid" />,
            },
          ]}
        >
          <Input
            addonBefore={prefixSelector}
            style={{ width: "100%" }}
            placeholder="Enter contact number"
          />
        </Form.Item>

        <Form.Item
          name="address"
          label="Address"
          rules={[
            {
              required: false,
              message: <IntlMessages id="error.address_required" />,
            },
          ]}
        >
          <Input.TextArea placeholder="Enter address" rows={4} />
        </Form.Item>

        <Form.Item
          name="region"
          label="Region"
          hasFeedback
          rules={[
            {
              required: true,
              message: <IntlMessages id="error.region_required" />,
            },
          ]}
        >
          <Select
            placeholder="Please select a region"
            showSearch
            optionFilterProp="children"
            filterOption={true}
            id="partner-region"
          >
            {countryList.map((country) => (
              <Option key={country.country_id} value={country.country_name}>
                {country.country_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Active Products:" className="mb-0">
          <Form.List name="active_products">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field, index) => (
                  <Form.Item
                    required={false}
                    key={field.key}
                  >
                    <Row gutter={24} align="middle" className="product-row" style={{ flexDirection: 'row' }}>
                      <Col span={8}>
                        <FormItem
                          {...field}
                          validateTrigger={["onChange", "onBlur"]}
                          name={[field.name, "product_id"]}
                          fieldKey={[field.fieldKey, "product_id"]}
                          rules={[{ required: true, message: "Please select product" }]}
                        >
                          <Select
                            placeholder="Please select product"
                            showSearch
                            optionFilterProp="children"
                            filterOption={true}
                            onChange={(productId) => {
                              // Get the product info to extract category
                              const productInfo = productList.results.find(p => p.product_id === productId);
                              // Reset status when product changes and set the category
                              form.setFieldsValue({
                                active_products: form.getFieldValue('active_products')?.map((p, i) => 
                                  i === field.name ? { ...p, product_id: productId, status: undefined, category: productInfo?.category } : p
                                )
                              });
                            }}
                          >
                            {productList.results.map((value) => (
                              <Option key={value.product_id} value={value.product_id}>
                                {value.product_name}
                              </Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>
                      <Col span={2}>
                        <FormItem
                          {...field}
                          name={[field.name, "category"]}
                          fieldKey={[field.fieldKey, "category"]}
                          hidden
                        >
                          <Input hidden />
                        </FormItem>
                      </Col>
                      <Col span={12}>
                        <FormItem
                          {...field}
                          validateTrigger={["onChange", "onBlur"]}
                          name={[field.name, "status"]}
                          fieldKey={[field.fieldKey, "status"]}
                          rules={[{ required: true, message: "Please select status" }]}
                        >
                          <Select placeholder="Please select product status">
                            {statusArray.map((value) => (
                              <Option key={value.id} value={value.id}>
                                {value.name}
                              </Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>
                      <Col span={2} style={{ textAlign: 'center' }}>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    className="add-button"
                  >
                    + Add Product
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Form.Item>

        <Form.List name="employee">
          {(fields, { add, remove }) => (
            <div>
              {fields.map((field, index) => (
                <Form.Item
                  label={`Person ${index + 1}:`}
                  required={false}
                  key={field.key}
                >
                  <Row gutter={24} align="middle" className="person-row" style={{ flexDirection: 'row' }}>
                    <Col span={7}>
                      <FormItem
                        {...field}
                        validateTrigger={["onChange", "onBlur"]}
                        name={[field.name, "first_name"]}
                        fieldKey={[field.fieldKey, "first_name"]}
                        rules={[{ required: true, message: "Please enter first name" }]}
                      >
                        <Input placeholder="first name" />
                      </FormItem>
                    </Col>
                    <Col span={7}>
                      <FormItem
                        {...field}
                        validateTrigger={["onChange", "onBlur"]}
                        name={[field.name, "last_name"]}
                        fieldKey={[field.fieldKey, "last_name"]}
                        rules={[{ required: true, message: "Please enter last name" }]}
                      >
                        <Input placeholder="last name" />
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem
                        {...field}
                        validateTrigger={["onChange", "onBlur"]}
                        name={[field.name, "contact_number"]}
                        fieldKey={[field.fieldKey, "contact_number"]}
                        rules={[
                          { required: true, message: "Please enter contact number" },
                          { len: 10, message: "Contact number must be 10 digits" },
                          { pattern: /^[0-9]+$/, message: "Must be a valid number" }
                        ]}
                      >
                        <Input placeholder="phone number" />
                      </FormItem>
                    </Col>
                    <Col span={2} style={{ textAlign: 'center' }}>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Col>
                  </Row>
                </Form.Item>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  className="add-button"
                >
                  + Add people
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
};

export default PartnerDrawer;